'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { useRouter } from 'next/navigation';
import { differenceInCalendarDays } from 'date-fns';

import {
  Alert,
  Badge as MokoBadge,
  Button as MokoButton,
  Separator,
} from '@mokoboko/components';
import { ERROR_ALERT_CLASS, ERROR_BADGE_CLASS } from '@/lib/error-ramp';
import { ArrowRightLeft } from 'lucide-react';
import useSubscriptionCodes, {
  type SubscriptionCode,
} from '@/state/subscription-codes';
import useClassPackCodes, { type ClassPackCode } from '@/state/class-pack-code';
import useTopupCodes from '@/state/topup-code';
import useOfferingAvailability from '@/hooks/use-offering-availability';
import { useCanChangePlan } from '@/hooks/use-can-change-plan';
import { FailedReadNotice, Illustration } from '@/components';
import { CreditSummaryCardSkeleton } from './credit-summary-card-skeleton';
import { BillingCounterTooltip } from './billing-counter-tooltip';
import { useAuth } from '@/contexts/auth';
import { ChevronDown } from '@/icons';
import {
  activateOnKeyDown,
  formatDateWithConditionalYear,
  mergeClassNames,
} from '@/lib/utils';
import {
  formatStatus,
  getStatusBadgeColor,
  getStatusTextClass,
  getSubscriptionRenewalLabel,
} from '@/helpers/status';
import {
  billingCounterUnit,
  findBillingSubscription,
  getOwnedSubscriptionPriceText,
  hasPaymentCounter,
  isAdminAddedSubscription,
  isEndingThisPeriod,
  isNeverRenewed,
  isSinglePayment,
  willResumeRecharge,
} from '@/helpers/subscription';
import {
  arePausedSubCreditsForfeited,
  getAvailableCredits,
  getAvailableSubCredits,
  getEffectiveClassesLeft,
  getSpendableClassesLeft,
  findTopupTargetCardId,
  isCanceledSubEnded,
  isDrainedPackHidden,
  isKnownSubscriptionCodeStatus,
  isTopupOfferReady,
  pluralizeCredits,
  sumActiveTopupCreditsBySource,
  summarizeTopupCredits,
} from '@/helpers/credits';
import { pendingPlanChangeSentence } from '@/helpers/plan-change';
import useTopup from '@/state/topup-content';
import useTopupEligibility from '@/state/topup';
import type { TopupSourceType } from '@/services/topup';
import { track, AnalyticsEvent } from '@/lib/analytics/tracker';

interface CreditSummaryCardProps {
  email: string;
  onReady?: () => void;
}

type DetailRow = {
  label: string;
  value: ReactNode;
  labelNode?: ReactNode;
};

type DesktopSubtitle = {
  parts: string[];
  billingCycleInfo?: {
    text: string;
    plan: SubscriptionCode['subscription'];
  };
};

type BadgeStatus = {
  label: string;
  color: 'green' | 'yellow' | 'red' | 'gray';
};

type CreditSummaryDerived = {
  activeSubs: SubscriptionCode[];
  canceledActiveSubs: SubscriptionCode[];
  pausedSubs: SubscriptionCode[];
  activePacks: ClassPackCode[];
  hasPacks: boolean;
  hasSubs: boolean;
  hasCanceledActiveSubs: boolean;
  hasPausedSubs: boolean;
  hasExpiredSubs: boolean;
  hasUnlimited: boolean;
  packCredits: number;
  subCredits: number;
  subscriptionTopupCredits: number;
  totalCredits: number;
  topupsNeverExpire: boolean;
  renewableSubs: SubscriptionCode[] | null;
  now: Date;
};

const isCombinedMode = (d: CreditSummaryDerived) =>
  d.hasPacks && (d.hasSubs || d.hasPausedSubs);

const getPrimarySub = (
  d: CreditSummaryDerived
): SubscriptionCode | undefined =>
  d.activeSubs[0] ?? d.canceledActiveSubs[0] ?? d.pausedSubs[0];

// With a pack on the card, bare "credits" is ambiguous; name the source.
const creditNoun = (count: number, hasPacks: boolean) =>
  `${hasPacks ? 'membership ' : ''}${pluralizeCredits(count)}`;

const renderPausedBanner = (d: CreditSummaryDerived) => {
  const sub = d.pausedSubs[0];
  if (arePausedSubCreditsForfeited(sub, d.now)) return null;
  const endsOn = sub.current_period_end
    ? formatDateWithConditionalYear(sub.current_period_end)
    : null;
  const available = getAvailableSubCredits(sub);
  if (available == null || !endsOn) return null;
  const creditsLeft = available + d.subscriptionTopupCredits;
  if (creditsLeft <= 0) return null;
  const isSingular = creditsLeft === 1;
  const noun = creditNoun(creditsLeft, d.hasPacks);
  const verb = isSingular ? 'expires' : 'expire';
  const pronoun = isSingular ? 'it' : 'them';
  if (sub.subscription.renewal_classes_behavior === 'RESET') {
    return (
      <Alert variant="warning" icon={null}>
        <span className="font-semibold">Heads up:</span> Your remaining{' '}
        {creditsLeft} {noun} {verb} on {endsOn}. Use {pronoun} before then.
      </Alert>
    );
  }
  return (
    <Alert variant="success" icon={null}>
      <span className="font-semibold">Good news:</span> Your {creditsLeft}{' '}
      {noun} will be ready when you resume.
      {willResumeRecharge(sub, d.now)
        ? ' Resuming now starts a new cycle and a new payment.'
        : null}
    </Alert>
  );
};

// A fixed-term plan carries the cancel from the moment its last payment is in
// (at purchase for a one-payment plan): it is running out as sold, not
// canceled, so it reads Active with an end date and offers no Reactivate.
// Once its period is over, the cancel it carries reads as canceled again, as
// the account card does.
const isCanceledSub = (sub: SubscriptionCode, now: Date): boolean =>
  !!sub.canceled_at && !isEndingThisPeriod(sub, now);

const subDisplayStatus = (sub: SubscriptionCode, now: Date): string =>
  isCanceledSub(sub, now)
    ? 'CANCELED'
    : sub.status === 'FULLY_USED'
      ? 'ACTIVE'
      : sub.status;

const endsDateText = (periodEnd: string): string =>
  `Ends ${formatDateWithConditionalYear(periodEnd)}`;

const canceledDateText = (
  sub: SubscriptionCode,
  periodEnd: string,
  now: Date
): string | null => {
  const label = getSubscriptionRenewalLabel(sub, now);
  return label && `${label} ${formatDateWithConditionalYear(periodEnd)}`;
};

const buildSubStatusLine = (
  sub: SubscriptionCode,
  isCanceled: boolean,
  now: Date
): { statusLabel: string; statusClass: string; datePart: string | null } => {
  const displayStatus = subDisplayStatus(sub, now);
  const statusLabel = isCanceled ? 'Canceled' : formatStatus(displayStatus);
  const statusClass = isCanceled
    ? 'text-error-600'
    : getStatusTextClass(displayStatus);
  let datePart: string | null = null;
  if (isCanceled && sub.current_period_end) {
    datePart = canceledDateText(sub, sub.current_period_end, now);
  } else if (isEndingThisPeriod(sub, now) && sub.current_period_end) {
    datePart = endsDateText(sub.current_period_end);
  } else if (sub.status === 'PAUSED') {
    const showExpiry = sub.subscription.renewal_classes_behavior === 'RESET';
    if (showExpiry && sub.current_period_end) {
      const isPast = new Date(sub.current_period_end) <= now;
      datePart = `${isPast ? 'Expired' : 'Expires'} ${formatDateWithConditionalYear(sub.current_period_end)}`;
    }
  } else if (
    sub.status !== 'PAST_DUE' &&
    sub.current_period_end &&
    !isNeverRenewed(sub)
  ) {
    datePart = `Renews ${formatDateWithConditionalYear(sub.current_period_end)}`;
  }
  return { statusLabel, statusClass, datePart };
};

const renderSubStatusLine = (parts: {
  statusLabel: string;
  statusClass: string;
  datePart: string | null;
}) => (
  <>
    <span className={mergeClassNames('font-medium', parts.statusClass)}>
      {parts.statusLabel}
    </span>
    {parts.datePart && (
      <>
        <span className="mx-1.5">·</span>
        {parts.datePart}
      </>
    )}
  </>
);

const renderCanceledBanner = (sub: SubscriptionCode, d: CreditSummaryDerived) => {
  const available = getAvailableSubCredits(sub);
  const creditsLeft =
    available == null ? null : available + d.subscriptionTopupCredits;
  const endsOn = sub.current_period_end
    ? formatDateWithConditionalYear(sub.current_period_end)
    : null;
  if (creditsLeft != null && creditsLeft > 0 && endsOn) {
    const isSingular = creditsLeft === 1;
    const noun = creditNoun(creditsLeft, d.hasPacks);
    const favorite = isSingular ? 'favorite' : 'favorites';
    const pronoun = isSingular ? 'it expires' : 'they expire';
    return (
      <Alert variant="error" icon={null} className={ERROR_ALERT_CLASS}>
        <span className="font-semibold">Ending soon:</span> You have{' '}
        {creditsLeft} {noun} left until {endsOn}. Book your {favorite} before{' '}
        {pronoun}!
      </Alert>
    );
  }
  if (endsOn) {
    return (
      <Alert variant="error" icon={null} className={ERROR_ALERT_CLASS}>
        <span className="font-semibold">Ending soon:</span>{' '}
        {d.hasPacks ? 'Your membership ends' : 'Your access ends'} {endsOn}.
        Reactivate anytime to keep booking.
      </Alert>
    );
  }
  return null;
};

const getPendingChangeDate = (
  codes: SubscriptionCode[] | undefined
): string | null => {
  const sub = (codes ?? []).find((c) => c.has_pending_plan_change);
  return sub?.current_period_end
    ? formatDateWithConditionalYear(sub.current_period_end)
    : null;
};

const buildBillingCycleRow = (
  sub: SubscriptionCode
): (DetailRow & { value: string }) | null => {
  if (!hasPaymentCounter(sub.subscription)) return null;
  const unit = billingCounterUnit(sub.subscription);
  return {
    label: unit,
    value: `${sub.cycles_consumed} of ${sub.subscription.number_of_billing_cycles}`,
    labelNode: (
      <div className="flex items-center gap-1">
        <p className="text-sm text-gray-700">{unit}</p>
        <BillingCounterTooltip plan={sub.subscription} />
      </div>
    ),
  };
};

const getCreditTitle = (d: CreditSummaryDerived): ReactNode => {
  if (d.hasUnlimited && d.packCredits > 0) {
    return `Unlimited + ${d.packCredits} ${pluralizeCredits(d.packCredits)}`;
  }
  if (d.hasUnlimited) {
    return 'Unlimited credits';
  }
  if (d.renewableSubs) {
    // Renewable gate keeps out CANCELED rows and cancels past their date, so x
    // is active + paused plan credits + folded subscription top-up credits.
    // Top-ups stack on top of the plan quota regardless of paused state, so x
    // can exceed y (e.g. 5 of 4).
    const x = d.subCredits;
    const y = d.renewableSubs.reduce(
      (sum, c) => sum + (c.subscription.total_number_of_classes_per_cycle ?? 0),
      0
    );
    return (
      <>
        <span className="text-xl">
          {x} of {y}
        </span>
        <span className="ml-2 font-sans text-sm font-normal text-gray-700 group-hover:text-red-400 group-data-[hover]:text-red-400">
          {pluralizeCredits(y)} left
        </span>
      </>
    );
  }
  if (d.totalCredits > 0) {
    return (
      <>
        <span className="text-xl">{d.totalCredits}</span>
        <span className="ml-2 font-sans text-sm font-normal text-gray-700 group-hover:text-red-400 group-data-[hover]:text-red-400">
          {pluralizeCredits(d.totalCredits)} left
        </span>
      </>
    );
  }
  return 'No credits';
};

const getBillingCycleInfo = (
  sub: SubscriptionCode
): DesktopSubtitle['billingCycleInfo'] => {
  const row = buildBillingCycleRow(sub);
  return row
    ? { text: `${row.label} ${row.value}`, plan: sub.subscription }
    : undefined;
};

const getPackExpiryText = (expiresAt: string, now: Date): string => {
  const daysLeft = differenceInCalendarDays(new Date(expiresAt), now);
  if (daysLeft === 0) return 'Expires today';
  if (daysLeft === 1) return 'Expires tomorrow';
  return `Expires in ${daysLeft} days`;
};

// Credits are shown as one total, so the only honest deadline for it is the
// first one that bites — not whichever pack happens to sort first.
const getEarliestPackExpiry = (packs: ClassPackCode[]): string | null => {
  const expiryDates = packs
    .map((pack) => pack.expires_at)
    .filter((date): date is string => !!date);
  if (!expiryDates.length) return null;
  return expiryDates.reduce((a, b) => (new Date(a) <= new Date(b) ? a : b));
};

const getPackExpiryLabel = (packs: ClassPackCode[]): string => {
  const earliest = getEarliestPackExpiry(packs);
  return earliest
    ? `Expires ${formatDateWithConditionalYear(earliest)}`
    : 'Never expires';
};

const getSubDateText = (
  sub: SubscriptionCode,
  isCanceled: boolean,
  now: Date
): string | null => {
  if (isCanceled && sub.current_period_end) {
    return canceledDateText(sub, sub.current_period_end, now);
  }
  if (isEndingThisPeriod(sub, now) && sub.current_period_end) {
    return endsDateText(sub.current_period_end);
  }
  if (sub.status === 'PAST_DUE') return 'Payment due';
  if (sub.current_period_end && !isNeverRenewed(sub)) {
    return `Renews ${formatDateWithConditionalYear(sub.current_period_end)}`;
  }
  return null;
};

// Catches subs whose status is CANCELED but canceled_at isn't populated:
// canceledActiveSubs filters by status alone, so we need both halves.
const isSubCanceled = (
  sub: SubscriptionCode,
  d: CreditSummaryDerived
): boolean =>
  (d.hasCanceledActiveSubs && d.activeSubs.length === 0) ||
  isCanceledSub(sub, d.now);

// Deadline is desktop-only: mobile rows are tight and banners repeat the date.
const getCombinedSubStatus = (
  sub: SubscriptionCode,
  d: CreditSummaryDerived
): { label: string; className: string; deadline: string | null } | null => {
  if (isSubCanceled(sub, d)) {
    return {
      label: 'Canceled',
      className: 'text-error-600',
      deadline:
        sub.current_period_end && new Date(sub.current_period_end) > d.now
          ? `Until ${formatDateWithConditionalYear(sub.current_period_end)}`
          : null,
    };
  }
  if (sub.status !== 'PAUSED') return null;
  const hasDeadline =
    sub.subscription.renewal_classes_behavior === 'RESET' &&
    !arePausedSubCreditsForfeited(sub, d.now) &&
    !!sub.current_period_end;
  return {
    label: 'Paused',
    className: getStatusTextClass('PAUSED'),
    deadline: hasDeadline
      ? `Until ${formatDateWithConditionalYear(sub.current_period_end!)}`
      : null,
  };
};

const buildCombinedSubRowValue = (
  sub: SubscriptionCode,
  d: CreditSummaryDerived
): ReactNode => {
  const status = getCombinedSubStatus(sub, d);
  const creditsText = d.hasUnlimited
    ? 'Unlimited'
    : `${d.subCredits} ${pluralizeCredits(d.subCredits)}`;
  if (!status) {
    const dateText = getSubDateText(sub, false, d.now);
    return dateText ? `${creditsText} · ${dateText}` : creditsText;
  }
  // A zero balance next to Paused/Canceled is noise; the status alone
  // explains why the subscription adds nothing to the total.
  const rest = d.hasUnlimited || d.subCredits > 0 ? creditsText : null;
  return (
    <>
      <span className={status.className}>{status.label}</span>
      {rest && ` · ${rest}`}
    </>
  );
};

const getCreditFallbackParts = (d: CreditSummaryDerived): string[] => {
  // Orphan top-up credits are still bookable, so describe them instead of
  // the dead plan; the dead-plan lines below are for the truly empty state.
  if (d.totalCredits > 0) {
    const pronoun =
      d.totalCredits === 1 ? 'it never expires' : 'they never expire';
    return d.topupsNeverExpire
      ? [`From your top-up, ${pronoun}`]
      : ['From your top-up'];
  }
  if (d.hasExpiredSubs) {
    return ['Membership expired. Get a new one?'];
  }
  return ['Get credits to start booking!'];
};

const getCreditDesktopSubtitle = (d: CreditSummaryDerived): DesktopSubtitle => {
  const combinedSub = isCombinedMode(d) ? getPrimarySub(d) : undefined;
  if (combinedSub) {
    const packCount = d.packCredits;
    const subText = d.hasUnlimited
      ? 'Unlimited membership credits'
      : `${d.subCredits} ${creditNoun(d.subCredits, true)}`;
    const status = getCombinedSubStatus(combinedSub, d);
    const subDetail = status
      ? `${status.label}${status.deadline ? ` · ${status.deadline}` : ''}`
      : getSubDateText(combinedSub, false, d.now);
    const subParenthetical = subDetail ? ` (${subDetail})` : '';
    return {
      parts: [
        `${packCount} pack ${pluralizeCredits(packCount)} (${getPackExpiryLabel(d.activePacks)}) + ${subText}${subParenthetical}`,
      ],
      billingCycleInfo: getBillingCycleInfo(combinedSub),
    };
  }

  if (d.hasSubs && !d.hasPacks) {
    const sub = d.activeSubs[0] ?? d.canceledActiveSubs[0];
    const isCanceled = isSubCanceled(sub, d);
    const parts: string[] = [];
    const dateText = getSubDateText(sub, isCanceled, d.now);
    if (dateText) parts.push(dateText);
    // After a multi-payment plan's last payment, its rate beside "Ends" would
    // read as a charge still to come; one payment shows what was paid.
    const priceText =
      isCanceled ||
      (isEndingThisPeriod(sub, d.now) && !isSinglePayment(sub.subscription))
        ? null
        : getOwnedSubscriptionPriceText(sub);
    if (priceText) parts.push(priceText);
    return { parts, billingCycleInfo: getBillingCycleInfo(sub) };
  }

  if (d.hasPausedSubs && !d.hasPacks && !d.hasSubs) {
    const sub = d.pausedSubs[0];
    const parts: string[] = [];
    const showExpiry = sub.subscription.renewal_classes_behavior === 'RESET';
    if (showExpiry && sub.current_period_end) {
      const isPast = new Date(sub.current_period_end) <= d.now;
      parts.push(
        `${isPast ? 'Expired' : 'Expires'} ${formatDateWithConditionalYear(sub.current_period_end)}`
      );
    }
    return { parts, billingCycleInfo: getBillingCycleInfo(sub) };
  }

  if (d.hasPacks && !d.hasSubs && !d.hasPausedSubs) {
    // Drop the "Class pack" lead-in when an expiry is shown — the date already
    // carries the line, and the icon + Active badge convey the rest. Keep it
    // only as a fallback when there's no date, so the subtitle isn't empty.
    const earliestExpiry = getEarliestPackExpiry(d.activePacks);
    if (earliestExpiry) {
      return { parts: [getPackExpiryText(earliestExpiry, d.now)] };
    }
    return { parts: ['Class pack'] };
  }

  return { parts: getCreditFallbackParts(d) };
};

const getCreditMobileSubtitle = (d: CreditSummaryDerived): ReactNode => {
  if (isCombinedMode(d)) {
    if (d.totalCredits === 0 && !d.hasUnlimited) return null;
    if (d.packCredits === 0) return 'All from membership';
    if (d.subCredits === 0 && !d.hasUnlimited) return 'All from class pack';
    return 'From membership and pack';
  }

  if (d.hasSubs && !d.hasPacks) {
    const sub = d.activeSubs[0] ?? d.canceledActiveSubs[0];
    return renderSubStatusLine(
      buildSubStatusLine(sub, isSubCanceled(sub, d), d.now)
    );
  }

  if (d.hasPausedSubs && !d.hasPacks && !d.hasSubs) {
    return renderSubStatusLine(
      buildSubStatusLine(d.pausedSubs[0], false, d.now)
    );
  }

  if (d.hasPacks && !d.hasSubs && !d.hasPausedSubs) {
    const earliestExpiry = getEarliestPackExpiry(d.activePacks);
    return renderSubStatusLine({
      statusLabel: 'Active',
      statusClass: getStatusTextClass('ACTIVE'),
      datePart: earliestExpiry
        ? getPackExpiryText(earliestExpiry, d.now)
        : null,
    });
  }

  return getCreditFallbackParts(d).join(' · ');
};

const getCreditBadgeStatus = (d: CreditSummaryDerived): BadgeStatus | null => {
  // Combined pack+sub: a single unscoped badge can't say which source it
  // describes, so statuses live in the subtitle/rows instead.
  if (isCombinedMode(d)) return null;
  if (d.activeSubs.length > 0) {
    const displayStatus = subDisplayStatus(d.activeSubs[0], d.now);
    return {
      label: formatStatus(displayStatus),
      color: getStatusBadgeColor(displayStatus),
    };
  }
  if (d.hasCanceledActiveSubs) {
    return { label: 'Canceled', color: 'red' };
  }
  if (d.hasPausedSubs) {
    return {
      label: formatStatus(d.pausedSubs[0].status),
      color: getStatusBadgeColor(d.pausedSubs[0].status),
    };
  }
  if (d.hasPacks) {
    return { label: 'Active', color: 'green' };
  }
  return null;
};

const getCreditDetailRows = (d: CreditSummaryDerived): DetailRow[] => {
  const rows: DetailRow[] = [];

  if (d.hasSubs) {
    const sub = d.activeSubs[0] ?? d.canceledActiveSubs[0];
    const row = buildBillingCycleRow(sub);
    if (row) rows.push(row);
  }

  if (d.hasPausedSubs && !d.hasSubs) {
    const row = buildBillingCycleRow(d.pausedSubs[0]);
    if (row) rows.push(row);
  }

  const combinedSub = isCombinedMode(d) ? getPrimarySub(d) : undefined;
  if (combinedSub) {
    rows.push({
      label: 'Membership',
      value: buildCombinedSubRowValue(combinedSub, d),
    });
    rows.push({
      label: 'Pack',
      value: `${d.packCredits} ${pluralizeCredits(d.packCredits)} · ${getPackExpiryLabel(d.activePacks)}`,
    });
  }

  return rows;
};

type TopupOffer = {
  source_type: TopupSourceType;
  price: number;
  currency: string;
};

// The whole summary card is a single tap target → /dashboard/credits, so the
// top-up nudge is plain text rather than a nested link (avoids mobile
// mis-taps). The click is attributed to the nudge when it's showing.
const goToCreditsFromCard = (
  router: ReturnType<typeof useRouter>,
  offer: TopupOffer | null
) => {
  if (offer) {
    track({
      event: AnalyticsEvent.TOPUP_CLICKED,
      properties: {
        surface: 'dashboard_summary',
        source_type: offer.source_type,
        quantity: 1,
        price: offer.price,
        currency: offer.currency,
      },
    });
  }
  router.push('/dashboard/credits');
};

// Impression of the top-up nudge. Lives in its own child so the effect runs
// without lifting the parent's credit derivation above its early returns.
const TopupOfferedTracker = ({ source_type, price, currency }: TopupOffer) => {
  useEffect(() => {
    track({
      event: AnalyticsEvent.TOPUP_OFFERED,
      properties: {
        surface: 'dashboard_summary',
        source_type,
        price,
        currency,
      },
    });
  }, [source_type, price, currency]);
  return null;
};

export const CreditSummaryCard = ({
  email,
  onReady,
}: CreditSummaryCardProps) => {
  const router = useRouter();
  const { user } = useAuth();
  const userEmail = user?.email ?? email;

  const {
    data: subscriptionCodes,
    isLoading: isSubsLoading,
    hasFailedRead: subsFailedRead,
  } = useSubscriptionCodes({ email: userEmail });
  const {
    data: classPackCodes,
    isLoading: isPacksLoading,
    hasFailedRead: packsFailedRead,
  } = useClassPackCodes({ email: userEmail });
  const {
    data: topupCodes,
    isLoading: isTopupsLoading,
    hasFailedRead: topupsFailedRead,
  } = useTopupCodes({ email: userEmail });

  const { data: topup } = useTopup();
  const { data: topupEligibility } = useTopupEligibility();

  const {
    hasSubscriptions: systemHasSubscriptions,
    hasClassPacks: systemHasClassPacks,
    isLoading: isOfferingsLoading,
  } = useOfferingAvailability();

  // Fresh per render so refetches re-judge forfeiture against the real clock;
  // a mount-frozen date made this card disagree with the credits page once a
  // paused sub's period end passed in a long-lived tab.
  const now = new Date();

  const hasAnyCanceledActiveSub = (subscriptionCodes ?? []).some(
    (c) =>
      isCanceledSub(c, now) &&
      c.current_period_end &&
      new Date(c.current_period_end) > now
  );

  const hasPendingPlanChange =
    !hasAnyCanceledActiveSub &&
    (subscriptionCodes ?? []).some((c) => c.has_pending_plan_change);

  const pendingChangeDate = hasPendingPlanChange
    ? getPendingChangeDate(subscriptionCodes)
    : null;

  const billingSubscription = findBillingSubscription(subscriptionCodes);
  const canChangePlan = useCanChangePlan(billingSubscription);

  const showChangePlan =
    !!billingSubscription &&
    canChangePlan &&
    (billingSubscription.status === 'ACTIVE' ||
      billingSubscription.status === 'FULLY_USED') &&
    !billingSubscription.canceled_at;

  const isLoading =
    isSubsLoading || isPacksLoading || isTopupsLoading || isOfferingsLoading;

  const readyFired = useRef(false);
  useEffect(() => {
    if (!isLoading && !readyFired.current) {
      readyFired.current = true;
      onReady?.();
    }
  }, [isLoading, onReady]);

  if (isLoading) {
    return <CreditSummaryCardSkeleton />;
  }

  if (subsFailedRead || packsFailedRead || topupsFailedRead) {
    return (
      <FailedReadNotice message="We couldn't load your credits. Please try again." />
    );
  }

  const activePacks = (classPackCodes ?? []).filter(
    (c) => c.status === 'ACTIVE'
  );
  // Spent packs the backend still serves: FULLY_USED but not yet past expires_at
  // (the cron flips them to EXPIRED and stops serving them). Most get hidden
  // below; the keepers are filtered once the top-up target is known.
  const rawUsedUpPacks = (classPackCodes ?? []).filter(
    (c) =>
      c.status === 'FULLY_USED' &&
      (!c.expires_at || new Date(c.expires_at) > now)
  );

  const activeSubs = (subscriptionCodes ?? []).filter(
    (c) =>
      c.status === 'ACTIVE' ||
      c.status === 'PAST_DUE' ||
      (c.status === 'FULLY_USED' &&
        c.current_period_end &&
        new Date(c.current_period_end) > now) ||
      // The four buckets below all test explicit statuses, so an unknown one
      // would fall through every list and take its credits with it. It may
      // still be billed, which is the judgement isVisibleSubscriptionCode
      // already encodes for the credits page.
      !isKnownSubscriptionCodeStatus(c.status)
  );
  const canceledActiveSubs = (subscriptionCodes ?? []).filter(
    (c) =>
      (c.status === 'CANCELED' || (c.status === 'PAUSED' && !!c.canceled_at)) &&
      c.current_period_end &&
      new Date(c.current_period_end) > now
  );
  const pausedSubs = (subscriptionCodes ?? []).filter(
    (c) => c.status === 'PAUSED' && !c.canceled_at
  );
  const expiredSubs = (subscriptionCodes ?? []).filter(
    (c) =>
      c.status === 'EXPIRED' ||
      c.status === 'DELETED' ||
      (c.status === 'CANCELED' &&
        (!c.current_period_end || new Date(c.current_period_end) <= now))
  );

  const packPlanCredits = activePacks.reduce(
    (sum, c) => sum + getAvailableCredits(c),
    0
  );
  const hasUnlimited = [
    ...activeSubs,
    ...canceledActiveSubs,
    ...pausedSubs.filter(
      (c) => !c.current_period_end || new Date(c.current_period_end) > now
    ),
  ].some((c) => c.classes_left === null);
  const { packTopupCredits, subscriptionTopupCredits } =
    sumActiveTopupCreditsBySource(topupCodes);
  const { neverExpires: topupsNeverExpire } = summarizeTopupCredits(
    topupCodes ?? []
  );

  // A drained pack stays only if it's the live top-up target (refillable) or
  // already holds top-up credits; otherwise it's dropped so its 0 balance can't
  // bleed into the tile's title/subtitle once another plan is active.
  const topupTargetCardId = findTopupTargetCardId(topupEligibility, [
    ...[...activePacks, ...rawUsedUpPacks].map((code) => ({
      type: 'pack' as const,
      code,
    })),
    ...[...activeSubs, ...canceledActiveSubs, ...pausedSubs].map((code) => ({
      type: 'subscription' as const,
      code,
    })),
  ]);
  const inDateUsedUpPacks = rawUsedUpPacks.filter(
    (c) =>
      !isDrainedPackHidden(
        c,
        c.class_pack_code_id === topupTargetCardId,
        topupCodes
      )
  );

  const displayPacks = [...activePacks, ...inDateUsedUpPacks];

  const totalSubCredits =
    [...activeSubs, ...canceledActiveSubs].reduce(
      (sum, c) => sum + (getSpendableClassesLeft(c, now) ?? 0),
      0
    ) + subscriptionTopupCredits;
  const pausedSubCredits = pausedSubs.reduce(
    (sum, c) => sum + (getEffectiveClassesLeft(c, now) ?? 0),
    0
  );

  const subCredits = totalSubCredits + pausedSubCredits;
  // Top-ups sit on top of the plan quota, so every pack-side surface has to
  // count them or the breakdown stops adding up to the headline.
  const packCredits = packPlanCredits + packTopupCredits;
  const totalCredits = packCredits + subCredits;

  const hasPacks = displayPacks.length > 0;
  const hasSubs = activeSubs.length > 0 || canceledActiveSubs.length > 0;
  const hasCanceledActiveSubs = canceledActiveSubs.length > 0;
  const hasPausedSubs = pausedSubs.length > 0;
  const hasExpiredSubs = expiredSubs.length > 0;

  // A plan bought off a direct link, or one the studio later hid, still needs
  // its cancel / pause / top-up controls, and this card is the only route to
  // them. Expired subs are excluded on purpose: past the grace period there is
  // nothing left to manage, so a non-public catalog hides the card again.
  const hasLivePlan =
    hasPacks ||
    hasSubs ||
    hasPausedSubs ||
    packTopupCredits + subscriptionTopupCredits > 0;

  if (!systemHasSubscriptions && !systemHasClassPacks && !hasLivePlan) {
    return null;
  }

  const activeAndPausedSubs = [...activeSubs, ...pausedSubs];
  const renewableSubs =
    !hasPacks &&
    !hasUnlimited &&
    canceledActiveSubs.length === 0 &&
    activeAndPausedSubs.length > 0 &&
    activeAndPausedSubs.every(
      (c) =>
        !isCanceledSubEnded(c, now) &&
        c.subscription.renewal_classes_behavior === 'RESET' &&
        typeof c.classes_left === 'number' &&
        typeof c.subscription.total_number_of_classes_per_cycle === 'number'
    )
      ? activeAndPausedSubs
      : null;

  const derived: CreditSummaryDerived = {
    activeSubs,
    canceledActiveSubs,
    pausedSubs,
    activePacks: displayPacks,
    hasPacks,
    hasSubs,
    hasCanceledActiveSubs,
    hasPausedSubs,
    hasExpiredSubs,
    hasUnlimited,
    packCredits,
    subCredits,
    subscriptionTopupCredits,
    totalCredits,
    topupsNeverExpire,
    renewableSubs,
    now,
  };

  // Dashboard nudge only when the balance is actually drained AND the plan
  // allows top-up (eligibility alone isn't enough: an ALWAYS-timed top-up is
  // eligible even with credits left — that path still works on
  // /dashboard/credits). No top-up → no CTA; re-purchase lives in the catalog.
  const topupOffer: TopupOffer | null =
    derived.totalCredits === 0 &&
    !derived.hasUnlimited &&
    !!topup &&
    isTopupOfferReady(topupEligibility)
      ? {
          source_type: topupEligibility.source_type,
          price: topupEligibility.price,
          currency: topupEligibility.currency,
        }
      : null;

  // No top-up available but a drained pack is still on the card → send them to
  // the catalog to browse a fresh pack (not re-buy the same one). Title already
  // reads "No credits", so the link stands alone with no prompt.
  const showNewPack =
    !topupOffer &&
    derived.totalCredits === 0 &&
    !derived.hasUnlimited &&
    derived.hasPacks &&
    systemHasClassPacks;

  const title = getCreditTitle(derived);
  const desktopSubtitle = getCreditDesktopSubtitle(derived);
  const mobileSubtitle = getCreditMobileSubtitle(derived);
  const badgeStatus = getCreditBadgeStatus(derived);
  const detailRows = getCreditDetailRows(derived);
  const showMobileDetails = detailRows.length > 0;

  const pendingChangeBanner = hasPendingPlanChange ? (
    <Alert variant="warning" icon={null}>
      <span className="font-semibold">Plan change scheduled.</span>{' '}
      {pendingPlanChangeSentence(pendingChangeDate)}
    </Alert>
  ) : null;

  const pausedBanner =
    hasPausedSubs && !hasSubs ? renderPausedBanner(derived) : null;

  const canceledSub = hasAnyCanceledActiveSub
    ? (activeSubs.find((c) => isCanceledSub(c, now)) ?? canceledActiveSubs[0])
    : null;
  const canceledBanner = canceledSub
    ? renderCanceledBanner(canceledSub, derived)
    : null;

  const hasBanner = !!(pendingChangeBanner || pausedBanner || canceledBanner);

  return (
    <div
      id="credit-summary-card"
      className={mergeClassNames(
        'mb-focus-visible group flex cursor-pointer flex-col rounded-xl bg-white px-5 py-4 shadow-skeu-secondary transition-all duration-200 hover:bg-gray-100 data-[hover]:bg-gray-100 sm:px-4',
        hasBanner && 'pb-5 sm:pb-4'
      )}
      onClick={() => goToCreditsFromCard(router, topupOffer)}
      role="button"
      tabIndex={0}
      onKeyDown={activateOnKeyDown(() => goToCreditsFromCard(router, topupOffer))}
    >
      {topupOffer && (
        <TopupOfferedTracker
          source_type={topupOffer.source_type}
          price={topupOffer.price}
          currency={topupOffer.currency}
        />
      )}
      <div className="flex items-center gap-4">
        <div className="order-last shrink-0 sm:order-first">
          <Illustration
            name="class-credits"
            alt=""
            width={128}
            height={128}
            quality={100}
            className="size-14 object-contain"
          />
        </div>

        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <div className="flex items-center gap-1.5">
            <p className="heading-card transition-colors duration-200 group-hover:text-red-400 group-data-[hover]:text-red-400">
              {title}
            </p>
            <span className="inline-flex transition-transform duration-300 ease-in-out group-hover:translate-x-1 group-data-[hover]:translate-x-1">
              <ChevronDown
                rotate={-90}
                className="h-5 w-5 shrink-0 text-red-400"
              />
            </span>
          </div>

          <div className="hidden flex-wrap items-center gap-1.5 text-sm text-gray-700 sm:flex">
            {desktopSubtitle.parts.map((part, i, arr) => (
              <span key={i} className="flex shrink-0 items-center gap-1.5">
                {part}
                {i < arr.length - 1 && <span className="text-gray-400">·</span>}
              </span>
            ))}
            {desktopSubtitle.billingCycleInfo && (
              <span className="flex shrink-0 items-center gap-1.5">
                {desktopSubtitle.parts.length > 0 && (
                  <span className="text-gray-400">·</span>
                )}
                {desktopSubtitle.billingCycleInfo.text}
                <BillingCounterTooltip
                  plan={desktopSubtitle.billingCycleInfo.plan}
                />
              </span>
            )}
            {topupOffer && (
              <span className="flex shrink-0 items-center gap-1.5">
                {(desktopSubtitle.parts.length > 0 ||
                  desktopSubtitle.billingCycleInfo) && (
                  <span className="text-gray-400">·</span>
                )}
                Need extra credits?{' '}
                <span className="font-semibold text-red-400">Top up</span>
              </span>
            )}
            {showNewPack && (
              <span className="flex shrink-0 items-center gap-1.5">
                {(desktopSubtitle.parts.length > 0 ||
                  desktopSubtitle.billingCycleInfo) && (
                  <span className="text-gray-400">·</span>
                )}
                <span className="font-semibold text-red-400">
                  Get a new pack
                </span>
              </span>
            )}
          </div>

          {mobileSubtitle && (
            <p className="text-sm text-gray-700 sm:hidden">{mobileSubtitle}</p>
          )}
        </div>

        {badgeStatus && (
          <div className="hidden shrink-0 sm:block">
            <MokoBadge
              label={badgeStatus.label}
              color={badgeStatus.color}
              className={
                badgeStatus.color === 'red' ? ERROR_BADGE_CLASS : undefined
              }
              size="md"
            />
          </div>
        )}
      </div>

      {pendingChangeBanner && (
        <div className="mt-4 hidden sm:block">{pendingChangeBanner}</div>
      )}
      {pausedBanner && (
        <div className="mt-4 hidden sm:block">{pausedBanner}</div>
      )}
      {canceledBanner && (
        <div className="mt-4 hidden sm:block">{canceledBanner}</div>
      )}

      {(showMobileDetails || topupOffer || showNewPack) && (
        <div className="mt-4 sm:hidden">
          <Separator />
          <div className="mt-4 flex flex-col gap-3">
            {showMobileDetails &&
              detailRows.map((row) => (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-4"
                >
                  {row.labelNode ?? (
                    <p className="shrink-0 text-sm text-gray-700">
                      {row.label}
                    </p>
                  )}
                  <p className="text-right text-sm font-semibold text-gray-900">
                    {row.value}
                  </p>
                </div>
              ))}
            {showMobileDetails && (topupOffer || showNewPack) && <Separator />}
            {topupOffer && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">Need extra credits?</p>
                <p className="text-sm font-semibold text-red-400">Top up</p>
              </div>
            )}
            {showNewPack && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-700">Out of credits?</p>
                <p className="text-sm font-semibold text-red-400">
                  Get a new pack
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {pendingChangeBanner && (
        <div className="mt-4 sm:hidden">{pendingChangeBanner}</div>
      )}
      {pausedBanner && <div className="mt-4 sm:hidden">{pausedBanner}</div>}
      {canceledBanner && <div className="mt-4 sm:hidden">{canceledBanner}</div>}

      {!hasSubs && !hasPausedSubs && showChangePlan && billingSubscription && (
        <div className="mt-4">
          <Separator />
          <div className="mt-4 flex items-center gap-3">
            <MokoButton
              variant="secondary-gray"
              size="md"
              leadingIcon={<ArrowRightLeft className="h-4 w-4" />}
              onClick={(e) => {
                e.stopPropagation();
                router.push(
                  `/dashboard/credits/change-plan?id=${billingSubscription.subscription_code_id}`
                );
              }}
            >
              Change plan
            </MokoButton>
          </div>
        </div>
      )}
    </div>
  );
};
